/**
 * description-youtube-lite.js
 *
 * Converts owner-pasted YouTube <iframe> embeds inside the product description
 * into <lite-youtube> click-to-load facades, so the ~1MB YouTube player bundle
 * is not loaded until the shopper actually clicks play.
 *
 * Scope: ONLY runs inside the product description / RTE container. It explicitly
 * skips anything inside the product media gallery, which is already handled by
 * video.liquid's lite-youtube rendering.
 *
 * Depends on lite-yt-embed.js + lite-yt-embed.css already being present in the
 * theme (loaded by video.liquid, or add a fallback loader — see bottom).
 */
(function () {
  'use strict';

  // Selectors where owner-pasted description HTML is rendered. Add/adjust to match
  // your theme. The first match that exists on the page is used; all matches are scanned.
  var DESCRIPTION_SELECTORS = [
    '.product__description',
    '.product-description',
    '.rte',
    '[data-product-description]'
  ];

  // Never touch iframes inside these — the gallery already handles its own videos.
  var EXCLUDE_WITHIN = [
    'media-gallery',
    '.product-media',
    '.product-information__media',
    'lite-youtube'
  ];

  /**
   * Extracts a YouTube video ID from the common URL formats owners paste.
   * Handles /embed/ID, youtu.be/ID, watch?v=ID, and nocookie variants.
   * @param {string} url
   * @returns {string|null}
   */
  function extractYouTubeId(url) {
    if (!url) return null;
    try {
      var u = new URL(url, window.location.origin);
      var host = u.hostname.replace(/^www\./, '');

      // youtu.be/VIDEOID
      if (host === 'youtu.be') {
        var short = u.pathname.split('/').filter(Boolean)[0];
        return short || null;
      }

      // youtube.com / youtube-nocookie.com
      if (host.indexOf('youtube.com') !== -1 || host.indexOf('youtube-nocookie.com') !== -1) {
        // /embed/VIDEOID  or  /v/VIDEOID
        var pathMatch = u.pathname.match(/\/(?:embed|v)\/([A-Za-z0-9_-]{6,})/);
        if (pathMatch) return pathMatch[1];

        // watch?v=VIDEOID
        var v = u.searchParams.get('v');
        if (v) return v;
      }
    } catch (e) {
      // Fall through to regex fallback below
    }

    // Last-ditch regex for anything the URL parser didn't catch
    var m = url.match(/(?:youtube(?:-nocookie)?\.com\/(?:embed|v)\/|youtu\.be\/|[?&]v=)([A-Za-z0-9_-]{6,})/);
    return m ? m[1] : null;
  }

  /**
   * Returns true if the element is inside a region we should not touch.
   * @param {Element} el
   * @returns {boolean}
   */
  function isExcluded(el) {
    for (var i = 0; i < EXCLUDE_WITHIN.length; i++) {
      if (el.closest(EXCLUDE_WITHIN[i])) return true;
    }
    return false;
  }

  /**
   * Replaces a single YouTube iframe with a <lite-youtube> facade.
   * @param {HTMLIFrameElement} iframe
   */
  function replaceIframe(iframe) {
    var id = extractYouTubeId(iframe.getAttribute('src') || iframe.getAttribute('data-src') || '');
    if (!id) return; // Can't parse — leave the original iframe untouched.

    var lite = document.createElement('lite-youtube');
    lite.setAttribute('videoid', id);
    lite.setAttribute('params', 'rel=0');
    lite.setAttribute('data-video-type', 'youtube');
    lite.classList.add('description-lite-youtube');

    // Preserve the iframe's title for accessibility if present.
    var title = iframe.getAttribute('title');
    if (title) lite.setAttribute('data-title', title);

    // Swap it in.
    if (iframe.parentNode) {
      iframe.parentNode.replaceChild(lite, iframe);
    }
  }

  function run() {
    // Confirm the custom element is available; if lite-yt-embed.js hasn't loaded,
    // creating <lite-youtube> is harmless but won't upgrade. We still swap — it will
    // upgrade whenever the script defines the element.
    var scanned = false;

    DESCRIPTION_SELECTORS.forEach(function (sel) {
      var containers = document.querySelectorAll(sel);
      containers.forEach(function (container) {
        if (isExcluded(container)) return;
        var iframes = container.querySelectorAll('iframe');
        iframes.forEach(function (iframe) {
          var src = iframe.getAttribute('src') || iframe.getAttribute('data-src') || '';
          if (src.indexOf('youtube') === -1 && src.indexOf('youtu.be') === -1) return;
          if (isExcluded(iframe)) return;
          replaceIframe(iframe);
          scanned = true;
        });
      });
    });

    return scanned;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();